import { defineStore } from 'pinia'
import {
  FEATURE_OPTIONS,
  type ArgQueryGovernmentSettings,
  type FeatureOptions
} from '@/types/accessor/api/governmentSetting/governmentSetting'
import { accessDataBasedOnNetwork } from '@/accessors/common/dataAccessorBasedOnNetwork'
import { useNetworkStore } from './networkStore'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'

// State 用 TypeScript 型定義
export interface GovernmentSettingState {
  /** 無償版用の調査上限総数 */
  upperLimitQuantityOfInspection: number | undefined
  /** 無償版用の機能制限フラグ */
  serviceLimitEnable: boolean | undefined
  /** 操作マニュアル動画のリンク */
  manualUrl: string | undefined
  /** デバイス認証用の機能制限フラグ */
  isDeviceAuthentication: boolean | undefined
  /** 自治体の契約に従った機能オプションの文字列配列 */
  featureOptions: FeatureOptions[]
}

// Pinia Store の定義
export const useGovernmentSettingStore = defineStore('governmentSetting', {
  state: (): GovernmentSettingState => ({
    upperLimitQuantityOfInspection: undefined,
    serviceLimitEnable: undefined,
    manualUrl: undefined,
    isDeviceAuthentication: undefined,
    featureOptions: []
  }),

  getters: {
    /**
     * State全体を取得
     */
    getGovernmentSetting(state): GovernmentSettingState {
      return state
    },

    /**
     * すべてのパラメータが undefined でない場合 true を返す
     * 通常、APIで取得後はすべてのフィールドが定義されることを期待
     */
    hasData(state): boolean {
      return (
        state.upperLimitQuantityOfInspection !== undefined &&
        state.serviceLimitEnable !== undefined &&
        state.manualUrl !== undefined &&
        state.isDeviceAuthentication !== undefined
      )
    }
  },

  actions: {
    /**
     * State をまとめて更新
     */
    updateState(obj: GovernmentSettingState) {
      this.upperLimitQuantityOfInspection = obj.upperLimitQuantityOfInspection
      this.serviceLimitEnable = obj.serviceLimitEnable
      this.manualUrl = obj.manualUrl
      this.isDeviceAuthentication = obj.isDeviceAuthentication
      this.featureOptions = obj.featureOptions
    },

    /**
     * State のすべての値を初期化（undefined に設定）
     */
    allUndefined() {
      this.upperLimitQuantityOfInspection = undefined
      this.serviceLimitEnable = undefined
      this.manualUrl = undefined
      this.isDeviceAuthentication = undefined
      this.featureOptions = []
    },

    /**
     * 自治体設定を API から取得
     */
    async getGovernmentSettings(params: { govCode: string; viewCode?: string }) {
      const networkStore = useNetworkStore()
      const governmentSettingQueryParams: ArgQueryGovernmentSettings = {
        system: 'disaster-emergency',
        govCode: params.govCode
      }

      const isOnline = networkStore.getIsOnline
      try {
        const resQueryGovernmentSetting = await accessDataBasedOnNetwork(
          'queryGovernmentSettingDaoApi',
          governmentSettingQueryParams,
          isOnline,
          () => {
            networkStore.setOfflineStatus()
          }
        )

        if (resQueryGovernmentSetting !== undefined) {
          const governmentSettingInfo = {
            upperLimitQuantityOfInspection:
              resQueryGovernmentSetting.upperLimitQuantityOfInspection,
            serviceLimitEnable: resQueryGovernmentSetting.serviceLimitEnable,
            manualUrl: resQueryGovernmentSetting.manualUrl,
            isDeviceAuthentication: resQueryGovernmentSetting.isDeviceAuthentication,
            featureOptions: resQueryGovernmentSetting.featureOptions ?? []
          }
          this.updateState(governmentSettingInfo)
          return governmentSettingInfo
        }
      } catch (err) {
        console.error(err)
        throw new GlobalError([ERROR_CODES.LOGIN_DISASTER_GET_FAILED])
      }
    },

    /**
     * 応急危険度アプリオプションの真偽値を取得する
     * @param なし
     * @returns 応急危険度アプリオプションの真偽値
     */
    isEmergencyAuth(): boolean {
      return this.featureOptions.includes(FEATURE_OPTIONS.emergencyAuth)
    }
  }
})
